import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Footer from '../components/Footer';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { API_URL } from '../lib/apiBase';
import {
  ArrowLeft,
  Rocket,
  AlertTriangle,
  BookOpen,
  DollarSign,
  Mail,
  Sparkles,
  Zap,
  Loader2,
  Send,
  CheckCircle2,
} from 'lucide-react';

const WeeklyDigestPage = () => {
  const navigate = useNavigate();
  const { currentWorkspaceId, currentWorkspace } = useWorkspace();
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);

  // Email subscription state
  const [subscribed, setSubscribed] = useState(false);
  const [subEmail, setSubEmail] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [subLoading, setSubLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const fetchSubscription = async (wsId) => {
    try {
      const res = await axios.get(`${API_URL}/api/digest-email/subscription`, {
        params: { workspace_id: wsId },
      });
      setSubscribed(!!res.data.subscribed);
      setSubEmail(res.data.email || '');
      setEmailInput(res.data.email || '');
    } catch (_e) {
      setSubscribed(false);
    }
  };

  useEffect(() => {
    if (!currentWorkspaceId) return;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/digest/${currentWorkspaceId}`);
        setDigest(res.data);
      } catch (err) {
        setDigest(null);
      } finally {
        setLoading(false);
      }
    })();
    fetchSubscription(currentWorkspaceId);
  }, [currentWorkspaceId]);

  const handleSubscribe = async () => {
    const email = emailInput.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error('Enter a valid email address');
      return;
    }
    try {
      setSubLoading(true);
      await axios.post(`${API_URL}/api/digest-email/subscribe`, {
        workspace_id: currentWorkspaceId,
        email,
      });
      setSubscribed(true);
      setSubEmail(email);
      toast.success('Subscribed — you\u2019ll get the Monday digest');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      setSubLoading(true);
      await axios.post(`${API_URL}/api/digest-email/unsubscribe`, {
        workspace_id: currentWorkspaceId,
      });
      setSubscribed(false);
      toast.success('Unsubscribed from the weekly digest');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unsubscribe');
    } finally {
      setSubLoading(false);
    }
  };

  const handleSendTest = async () => {
    try {
      setSendingTest(true);
      const res = await axios.post(`${API_URL}/api/digest-email/send-test`, {
        workspace_id: currentWorkspaceId,
      });
      toast.success(`Test digest sent to ${res.data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const Section = ({ title, Icon, items, empty, renderItem }) => (
    <Card className="p-5" data-testid={`digest-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <h3 className="font-semibold">{title}</h3>
        </div>
        <Badge variant="outline">{items?.length || 0}</Badge>
      </div>
      {(!items || items.length === 0) ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="text-sm border-l-2 border-primary/30 pl-3 py-1">
              {renderItem(it)}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="weekly-digest-page">
      <header className="border-b bg-card sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/app')} data-testid="digest-back">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              Weekly Digest
            </h1>
            <Badge variant="secondary" className="text-xs">Email + live preview</Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Email subscription card — delivery is live via Resend */}
        <Card className="p-5 mb-6" data-testid="digest-subscribe-card">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <Mail className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold flex items-center gap-2">
                Get this in your inbox every Monday
                {subscribed && (
                  <Badge variant="secondary" className="gap-1 text-emerald-600">
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </Badge>
                )}
              </h3>
              <p className="text-sm text-muted-foreground mt-1 mb-3">
                {subscribed
                  ? `We\u2019ll email ${subEmail} a roundup of new Build-lane items every Monday.`
                  : 'Subscribe your inbox to receive a weekly roundup of new Build-lane items and shipped work.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1">
                  <Label htmlFor="digest-email" className="sr-only">Email</Label>
                  <Input
                    id="digest-email"
                    type="email"
                    placeholder="you@founder.email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    disabled={subscribed}
                    data-testid="digest-email-input"
                  />
                </div>
                {!subscribed ? (
                  <Button onClick={handleSubscribe} disabled={subLoading} data-testid="digest-subscribe-btn">
                    {subLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleSendTest}
                      disabled={sendingTest}
                      data-testid="digest-send-test-btn"
                    >
                      {sendingTest ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      ) : (
                        <Send className="h-4 w-4 mr-1.5" />
                      )}
                      Send test
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleUnsubscribe}
                      disabled={subLoading}
                      data-testid="digest-unsubscribe-btn"
                    >
                      Unsubscribe
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Transactional email for your own workspace. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </Card>

        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium mb-1">This is exactly what your Monday digest email looks like.</p>
            <p className="text-muted-foreground">
              The live preview below updates in real time and mirrors the email we send.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading digest…
          </div>
        ) : !digest ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">No workspace selected or digest unavailable.</p>
          </Card>
        ) : (
          <>
            {/* Hero summary */}
            <Card className="p-6 mb-6" data-testid="digest-summary">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center text-xl bg-primary/10">
                  {digest.workspace.icon || '📦'}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{digest.workspace.name}</h2>
                  <p className="text-xs text-muted-foreground">
                    Last 7 days
                  </p>
                </div>
              </div>
              <p className="text-base font-medium mt-4" data-testid="digest-summary-text">{digest.summary_text}</p>
              {digest.pipeline_mrr_estimate > 0 && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-semibold tabular-nums">${digest.pipeline_mrr_estimate.toLocaleString()}/mo</span>
                  <span className="text-muted-foreground">in planned pipeline value</span>
                </div>
              )}
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              <Section
                title="New Build-Next"
                Icon={Rocket}
                items={digest.new_build_next}
                empty="Nothing new this week."
                renderItem={(d) => <span className="font-medium">{d.summary}</span>}
              />
              <Section
                title="New Fix-Now"
                Icon={AlertTriangle}
                items={digest.new_fix_now}
                empty="No churn risks flagged."
                renderItem={(d) => <span className="font-medium">{d.summary}</span>}
              />
              <Section
                title="Shipped this week"
                Icon={BookOpen}
                items={digest.shipped_this_week}
                empty="Nothing shipped yet — ship a decision to see it here."
                renderItem={(d) => (
                  <div>
                    <div className="font-medium">{d.summary}</div>
                    <div className="text-xs text-muted-foreground">Category: {d.category}</div>
                  </div>
                )}
              />
              <Section
                title="Top quick wins"
                Icon={Zap}
                items={digest.quick_wins}
                empty="No open quick wins."
                renderItem={(d) => (
                  <div>
                    <div className="font-medium">{d.summary}</div>
                    {d.roi_per_hour && (
                      <div className="text-xs text-primary">${Math.round(d.roi_per_hour).toLocaleString()}/hour ROI</div>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="mt-8 text-center">
              <Button onClick={() => navigate('/app')} className="btn-cta" data-testid="digest-open-dashboard">
                Open dashboard
              </Button>
            </div>
          </>
        )}
      </main>

      <Footer variant="minimal" />
    </div>
  );
};

export default WeeklyDigestPage;
